import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Source, Layer, Popup, useMap } from 'react-map-gl/maplibre';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import ListItemIcon from '@mui/material/ListItemIcon';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import LayersIcon from '@mui/icons-material/Layers';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import IconButton from '@mui/material/IconButton';
import MapIcon from '@mui/icons-material/Map';
import Grid4x4Icon from '@mui/icons-material/Grid4x4';
import {
  ConvectionTooltip, IceTooltip, TurbulenceTooltip, DustTooltip, formatCoordinates
} from '../common/MapTooltip';
import { buttonLabels } from '../../config/layerConfig';
import VisibilityGraph from './visibility-layer/visibility_graph';
import axios from 'axios';

import Badge from '@mui/material/Badge';


// --- 🧠 HOOK IMPERATIVO DE DOUBLE BUFFERING (Replicado de ContrailMap.js) ---
const useBufferedWmsLayers = (mapInstance, { activeLayer, time, opacity, tileSize, enabled }) => {
  const layerCache = useRef(new Set());
  const prevActiveLayerRef = useRef(activeLayer);
  const MAX_CACHE = 6;

  // Construye la URL WMTS/WMS según la capa
  const buildUrl = useCallback((layer, timeIso) => {
    if (!timeIso) return null;
    if (layer === 'CNN:EU_025_live_demo') {
      return `${process.env.REACT_APP_API_URL}/proxy/wmts/cnn-live?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=CNN:cnn_025_live_demo&STYLE=&TILEMATRIX=EPSG:900913:{z}&TILEMATRIXSET=EPSG:900913&FORMAT=image/png8&TILECOL={x}&TILEROW={y}&TIME=${encodeURIComponent(timeIso)}`;
    }
    return null;
  }, []);

  // Cleanup: eliminar todas las capas imperativamente creadas
  const cleanupAll = useCallback((nativeMap) => {
    try {
      layerCache.current.forEach(cachedLayerId => {
        if (nativeMap.getLayer(cachedLayerId)) nativeMap.removeLayer(cachedLayerId);
        const srcId = cachedLayerId.replace('wms-lyr-', 'wms-src-');
        if (nativeMap.getSource(srcId)) nativeMap.removeSource(srcId);
      });
      layerCache.current.clear();
    } catch (e) { /* silencio */ }
  }, []);

  // Limpiar al cambiar de capa activa (ej: de CNN a ICE)
  useEffect(() => {
    if (prevActiveLayerRef.current !== activeLayer) {
      const nativeMap = mapInstance?.getMap ? mapInstance.getMap() : mapInstance;
      if (nativeMap && nativeMap.style) {
        cleanupAll(nativeMap);
      }
      prevActiveLayerRef.current = activeLayer;
    }
  }, [activeLayer, mapInstance, cleanupAll]);

  // Efecto principal: crear/mostrar/ocultar capas
  useEffect(() => {
    if (!enabled) return;
    const nativeMap = mapInstance?.getMap ? mapInstance.getMap() : mapInstance;
    if (!nativeMap || !nativeMap.style || !time) return;

    const safeId = time.replace(/[:.]/g, '-');
    const sourceId = `wms-src-${safeId}`;
    const layerId = `wms-lyr-${safeId}`;
    const url = buildUrl(activeLayer, time);
    if (!url) return;

    const updateMapState = () => {
      try {
        if (!nativeMap.style) return;

        // 1. Crear source+layer si no existen
        if (!nativeMap.getSource(sourceId)) {
          nativeMap.addSource(sourceId, { type: 'raster', tiles: [url], tileSize: tileSize || 256 });
          nativeMap.addLayer({
            id: layerId, type: 'raster', source: sourceId,
            paint: { 'raster-opacity': 0, 'raster-fade-duration': 0 }
          });
          layerCache.current.add(layerId);
        }

        // 2. Activar capa actual (mostrar con opacidad target)
        if (nativeMap.getLayer(layerId)) {
          nativeMap.setPaintProperty(layerId, 'raster-opacity', opacity);
        }

        // 3. Ocultar todas las demás capas cacheadas
        layerCache.current.forEach(cachedId => {
          if (cachedId !== layerId && nativeMap.getLayer(cachedId)) {
            nativeMap.setPaintProperty(cachedId, 'raster-opacity', 0);
          }
        });

        // 4. Limpieza si excede el tamaño máximo de caché
        if (layerCache.current.size > MAX_CACHE) {
          const oldest = layerCache.current.values().next().value;
          if (oldest && oldest !== layerId) {
            if (nativeMap.getLayer(oldest)) nativeMap.removeLayer(oldest);
            const oldSrc = oldest.replace('wms-lyr-', 'wms-src-');
            if (nativeMap.getSource(oldSrc)) nativeMap.removeSource(oldSrc);
            layerCache.current.delete(oldest);
          }
        }
      } catch (e) { /* Silencio en el loop de renderizado */ }
    };

    requestAnimationFrame(updateMapState);
  }, [mapInstance, activeLayer, time, opacity, tileSize, enabled, buildUrl]);

  // Cleanup al desmontar
  useEffect(() => {
    return () => {
      const nativeMap = mapInstance?.getMap ? mapInstance.getMap() : mapInstance;
      if (nativeMap && nativeMap.style) {
        cleanupAll(nativeMap);
      }
    };
  }, [mapInstance, cleanupAll]);
};

// Convierte "pred_20250704T010000Z.pmtiles" → "2025-07-04T01:00:00Z"
function filenameToIso(filename) {
  const m = filename?.match(/(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z/);
  if (!m) return null;
  return `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6]}Z`;
}

// Elige el fichero PMTiles para un instante ISO: coincidencia exacta y, si no,
// el frame disponible más cercano (evita congelar el heatmap si el slider no
// cae justo en un frame del modelo, p.ej. turbulence).
export function pickPmtileForTime(files, isoTime) {
  if (!files?.length || !isoTime) return null;
  const clean = isoTime.replace(/[-:]/g, '');
  const exact = files.find(f => f.includes(clean));
  if (exact) return exact;

  const targetMs = Date.parse(isoTime);
  if (!Number.isFinite(targetMs)) return null;
  return files.reduce((best, f) => {
    const ms = Date.parse(filenameToIso(f));
    if (!Number.isFinite(ms)) return best;
    const d = Math.abs(ms - targetMs);
    return !best || d < best.d ? { f, d } : best;
  }, null)?.f ?? null;
}

// Mapa de capa activa → clave del endpoint PMTiles
const PMTILES_LAYER_MAP = {
  'CNN:EU_025_live_demo': 'cnn_025_live',   // nombre de la capa de PMTILES que se leera
  'CNN:EU_01_live_demo': 'cnn_01_live',
  'TURBULENCE:TURBULENCE_0125_live_demo': 'turbulence_live',
  'ICE:ICE_025_live_demo': 'ice_live',
};
const formatDateForDisplay = (date) => {
  if (!date) return "";
  const d = new Date(date);
  const year = d.getUTCFullYear();
  const month = (d.getUTCMonth() + 1).toString().padStart(2, "0");
  const day = d.getUTCDate().toString().padStart(2, "0");
  const hours = d.getUTCHours().toString().padStart(2, "0");
  const minutes = d.getUTCMinutes().toString().padStart(2, "0");
  const seconds = d.getUTCSeconds().toString().padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds} UTC`;
};
const MapLayers = ({ activeMapBaseLayer, layerOpacity, cqlFilter, onLayerChange, onInitialTimeChange, forecastValue, showGeoJson, setShowGeoJson, showMosaic, setShowMosaic, showFirs, setShowFirs }) => {
  const { current: map } = useMap(); // Moved to top for scope availability
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeLayer, setActiveLayer] = useState('CNN:EU_025_live_demo');
  const [hoverInfo, setHoverInfo] = useState(null);
  const [clickedInfo, setClickedInfo] = useState(null);


  // PMTiles data state
  const [pmtilesConfig, setPmtilesConfig] = useState(null);
  const [currentPmtileFile, setCurrentPmtileFile] = useState(null);

  // Fetch PMTiles list cuando cambia la capa activa (si es una capa PMTiles)
  useEffect(() => {
    // Fuera la config de la capa anterior: si no, su heatmap se sigue pintando bajo la nueva capa hasta que llega
    // la nueva lista, o para siempre si la petición falla (p.ej. ice_live sin ficheros todavía)
    setPmtilesConfig(null);
    setCurrentPmtileFile(null);
    const pmtilesKey = PMTILES_LAYER_MAP[activeLayer];
    if (!pmtilesKey) return;

    let stale = false; // la lista de una capa que ya no está activa no se aplica
    const fetchPmtilesList = async () => {
      try {
        const resp = await axios.get(`${process.env.REACT_APP_API_URL}/api/pmtiles/list?layer=${pmtilesKey}`);
        if (stale) return;
        setPmtilesConfig(resp.data);
        setCurrentPmtileFile(resp.data.initialFile);

        // Notificar al padre con la hora inicial del fichero PMTiles
        const isoDate = filenameToIso(resp.data.initialFile);
        if (isoDate && onInitialTimeChange) {
          onInitialTimeChange(isoDate);
        }
      } catch (err) {
        console.error("Error fetching PMTiles list:", err);
      }
    };
    fetchPmtilesList();
    return () => { stale = true; };
  }, [activeLayer, onInitialTimeChange]);

  // --- DEBOUNCING SYSTEM ---
  const [debouncedCqlFilter, setDebouncedCqlFilter] = useState(cqlFilter);
  const is15mLayer = activeLayer.startsWith('NOWCASTING') || activeLayer.includes('_15m');

  useEffect(() => {
    const delay = is15mLayer ? 300 : 200;
    const timer = setTimeout(() => {
      setDebouncedCqlFilter(cqlFilter);
    }, delay);
    return () => clearTimeout(timer);
  }, [cqlFilter, is15mLayer]);

  // --- EXTRAER TIME ISO DESDE EL CQL FILTER ---
  const timeFromFilter = useMemo(() => {
    const m = debouncedCqlFilter?.match(/(?:FORECAST_RELEASE|VALID_TIMESTAMP)\s*=\s*'([^']+)'/);
    if (!m) return null;
    let t = m[1].trim().replace(' ', 'T');
    if (!t.endsWith('Z')) t += 'Z';
    return t.split('.')[0].replace('Z', '') + 'Z';
  }, [debouncedCqlFilter]);

  // Update current PMTile file based on timeFromFilter
  useEffect(() => {
    if (!pmtilesConfig || !timeFromFilter || !PMTILES_LAYER_MAP[activeLayer]) return;

    // Coincidencia exacta y, si no, frame más cercano. Sin esto el heatmap se
    // congela cuando el slider no cae justo en un frame del modelo (turbulence:
    // el slider arranca de ISSUE_TIMESTAMP, no del primer frame PMTiles).
    // ponytail: nearest-frame; la causa de fondo es el doble origen de forecastValue
    // (ForecastRelease WFS vs initialFile PMTiles). Unificar esos dos si se quiere match exacto.
    const matchingFile = pickPmtileForTime(pmtilesConfig.files, timeFromFilter);

    if (matchingFile) {
      setCurrentPmtileFile(matchingFile);
    } else {
      console.warn('[PMTiles] No file matched for time:', timeFromFilter, '| Available:', pmtilesConfig.files);
    }
  }, [timeFromFilter, pmtilesConfig, activeLayer]);

  // Datos GeoJSON en memoria
  const [geoJsonData, setGeoJsonData] = useState(null);

  const [windowWidth, setWindowWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 1200);

  // Resize y Handlers
  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const handleMenuItemClick = (layerName) => {
    if (layerName !== activeLayer) {
      setActiveLayer(layerName);
      onLayerChange(layerName);
      // Immediate clean-up to avoid "ghost" tiles or data
      setHoverInfo(null);
      setClickedInfo(null);
      setGeoJsonData(null);
      setShowGeoJson(false);
    }
    handleClose();
  };

  const getAdaptedCqlFilter = (filterString, currentLayer) => {
    if (!filterString) return '';
    if (currentLayer.startsWith('CNN')) return filterString;

    const parts = filterString.split(/\s+AND\s+/i);
    const validParts = parts.filter(part => {
      if (part.includes('TOP')) return false;
      if (part.includes('CONVECTIVE_MATRIX_RISK')) return false;
      return true;
    });
    return validParts.join(' AND ');
  };

  const isGeoJsonLayer = [
    'CNN:EU_025_live_demo', 'CNN:EU_01_live_demo', 'CNN:euro_1k_00625_15m',
    'ICE:ICE_025_live_demo', 'TURBULENCE:TURBULENCE_0125_live_demo',
    'DUST:DUST_025_live_demo', 'VISIBILITY:visibility_live_demo'
  ].includes(activeLayer);

  const isCnnLivePmtiles = activeLayer === 'CNN:EU_025_live_demo' || activeLayer === 'CNN:EU_01_live_demo';
  const hasHeatmap = !!PMTILES_LAYER_MAP[activeLayer];
  const isCnnLayer = activeLayer.startsWith('CNN');
  // Turbulence e ICE: polígonos desactivados ("coming soon"), su WFS live está parado (2026-06-18 / 2026-03-06)
  const polygonsComingSoon = activeLayer.startsWith('TURBULENCE') || activeLayer.startsWith('ICE');
  const isNowcasting = activeLayer === 'NOWCASTING:nowcastnet_live_demo';

  // ¿Se van a dibujar polígonos WFS? En capas con heatmap solo si el toggle está activo.
  // Turbulence e ICE tienen los polígonos desactivados → no se pide el WFS, solo los PMTiles.
  const showPolygons = isGeoJsonLayer && (hasHeatmap ? showGeoJson : true);


  // --- 🧠 DOUBLE BUFFERING IMPERATIVO (Técnica ContrailMap) ---
  // Desactivado para PMTiles
  /*
  useBufferedWmsLayers(map, {
    activeLayer,
    time: timeFromFilter,
    opacity: layerOpacity || 0.75,
    tileSize: 256,
    enabled: isCnnLiveWms,
  });
  */

  // --- WFS DATA (Debounced & Abortaable) ---
  const dataUrl = useMemo(() => {
    const adaptedFilter = getAdaptedCqlFilter(debouncedCqlFilter, activeLayer);
    const encodedFilter = encodeURIComponent(adaptedFilter);

    if (showPolygons) {
      const baseUrl = activeLayer.startsWith('CNN')
        ? `${process.env.REACT_APP_API_URL}/proxy/wfs/cloud`
        : `${process.env.REACT_APP_API_URL}/proxy/wfs`;

      return `${baseUrl}?service=WFS&version=1.0.0&request=GetFeature&typeName=${activeLayer}&outputFormat=application/json&CQL_FILTER=${encodedFilter}`;
    }
    return null;
  }, [activeLayer, debouncedCqlFilter, showPolygons]);

  useEffect(() => {
    if (!dataUrl) return;

    const controller = new AbortController();

    fetch(dataUrl, { signal: controller.signal })
      .then(res => res.json())
      .then(data => {
        if (!data.features) return;
        const sortedFeatures = [...data.features].sort((a, b) => {
          const riskA = a.properties.CONVECTIVE_MATRIX_RISK || a.properties.ICE_MATRIX_RISK || a.properties.TURBULENCE_MATRIX_RISK || a.properties.DUST_MATRIX_RISK || 0;
          const riskB = b.properties.CONVECTIVE_MATRIX_RISK || b.properties.ICE_MATRIX_RISK || b.properties.TURBULENCE_MATRIX_RISK || b.properties.DUST_MATRIX_RISK || 0;
          return riskA - riskB;
        });
        setGeoJsonData({ ...data, features: sortedFeatures });
      })
      .catch(err => {
        if (err.name !== 'AbortError') console.error("WFS Error:", err);
      });

    return () => controller.abort();
  }, [dataUrl]);

  // Vector Source data
  const vectorSourceData = useMemo(() => {
    if (isGeoJsonLayer) {
      return geoJsonData || { type: 'FeatureCollection', features: [] };
    }
    return null;
  }, [isGeoJsonLayer, geoJsonData]);

  // --- ESTILOS ---
  const opacity = layerOpacity || 0.75;

  // --- INTERACCIONES ---
  useEffect(() => {
    if (!map || !isGeoJsonLayer) return;

    // Identificar el ID de la capa activa para interacciones
    let layerId = '';
    if (activeLayer.startsWith('VISIBILITY')) layerId = 'visibility-circle';
    else if (activeLayer.startsWith('CNN')) layerId = 'cnn-fill';
    else if (activeLayer.startsWith('ICE')) layerId = 'ice-fill';
    else if (activeLayer.startsWith('TURBULENCE')) layerId = 'turbulence-fill';
    else if (activeLayer.startsWith('DUST')) layerId = 'dust-fill';

    if (!layerId) return;

    const onMapMouseMove = (e) => {
      if (!map.getLayer(layerId)) return;
      const features = map.queryRenderedFeatures(e.point, { layers: [layerId] });
      if (features.length > 0) {
        map.getCanvas().style.cursor = 'pointer';
        setHoverInfo({ lngLat: e.lngLat, feature: features[0], layer: activeLayer });
      } else {
        map.getCanvas().style.cursor = '';
        setHoverInfo(null);
      }
    };

    const onMapClick = (e) => {
      if (!map.getLayer(layerId)) return;
      if (activeLayer.startsWith('VISIBILITY')) {
        const features = map.queryRenderedFeatures(e.point, { layers: [layerId] });
        if (features.length > 0) {
          setClickedInfo({ lngLat: e.lngLat, feature: features[0] });
        } else {
          setClickedInfo(null);
        }
      }
    };

    map.on('mousemove', onMapMouseMove);
    map.on('click', onMapClick);
    return () => {
      if (map) {
        map.off('mousemove', onMapMouseMove);
        map.off('click', onMapClick);
        if (map.getCanvas()) map.getCanvas().style.cursor = '';
      }
    };
  }, [map, activeLayer, isGeoJsonLayer]);

  // --- WMS GetFeatureInfo CLICK (CNN live 025) ---
  const [wmsClickLoading, setWmsClickLoading] = useState(false);

  const onWmsClick = useCallback(async (e) => {
    if (!map) return;

    // Usar mapa nativo para project/getBounds/getCanvas
    const nativeMap = map.getMap ? map.getMap() : map;

    const point = nativeMap.project(e.lngLat);
    const bounds = nativeMap.getBounds();
    const canvas = nativeMap.getCanvas();

    // Extraer TIME del cqlFilter (mismo cálculo que getWmsUrl)
    const timeslotMatch = cqlFilter?.match(/(?:FORECAST_RELEASE|VALID_TIMESTAMP)\s*=\s*'([^']+)'/);
    const timeslot = timeslotMatch ? timeslotMatch[1] : '';
    let timeslotIso = '';
    if (timeslot) {
      timeslotIso = timeslot.trim().replace(' ', 'T');
      if (!timeslotIso.endsWith('Z')) timeslotIso += 'Z';
      timeslotIso = timeslotIso.split('.')[0].replace('Z', '') + 'Z';
    }

    const baseUrl = `${process.env.REACT_APP_API_URL}/proxy/wms/cnn-live-info`;
    const bboxStr = `${bounds.getWest()},${bounds.getSouth()},${bounds.getEast()},${bounds.getNorth()}`;
    const featureInfoUrl = `${baseUrl}?service=WMS&version=1.1.1&request=GetFeatureInfo&layers=CNN:cnn_025_live_demo&query_layers=CNN:cnn_025_live_demo&info_format=application/json&x=${Math.round(point.x)}&y=${Math.round(point.y)}&width=${canvas.width}&height=${canvas.height}&srs=EPSG:4326&bbox=${bboxStr}&TIME=${timeslotIso}`;

    try {
      setWmsClickLoading(true);
      console.log('[GetFeatureInfo] Requesting:', featureInfoUrl);
      const resp = await axios.get(featureInfoUrl);
      console.log('[GetFeatureInfo] Response:', resp.data);
      if (resp.data && resp.data.features && resp.data.features.length > 0) {
        setClickedInfo({ lngLat: e.lngLat, feature: resp.data.features[0], source: 'wms' });
      } else {
        setClickedInfo(null);
      }
    } catch (err) {
      console.error('Error GetFeatureInfo (CNN live):', err);
    } finally {
      setWmsClickLoading(false);
    }
  }, [map, cqlFilter]);

  useEffect(() => {
    if (!map) return;
    const nativeMap = map.getMap ? map.getMap() : map;

    const handleInteraction = () => {
      const zoom = nativeMap.getZoom();
      if (isCnnLivePmtiles && zoom >= 4) {
        nativeMap.getCanvas().style.cursor = 'grab';
        // PMTiles use vector layers, interaction is handled in move/click handlers above
      } else {
        if (isCnnLivePmtiles) {
          nativeMap.getCanvas().style.cursor = '';
        }
      }
    };

    if (isCnnLivePmtiles) {
      handleInteraction();
      nativeMap.on('zoom', handleInteraction);
      return () => {
        nativeMap.off('zoom', handleInteraction);
        if (nativeMap.getCanvas()) nativeMap.getCanvas().style.cursor = '';
      };
    }
  }, [map, isCnnLivePmtiles, onWmsClick]);

  // --- POPUPS ---
  const renderPopup = () => {
    if (!hoverInfo || !hoverInfo.feature) return null;
    const props = hoverInfo.feature.properties;
    const { lng, lat } = hoverInfo.lngLat;
    let content = null;

    if (activeLayer.startsWith('VISIBILITY')) {
      const airport = props.AIRPORT || 'N/A';
      const riskVal = parseInt(props.VISIBILITY_MATRIX_RISK, 10);
      const circleColor = (() => {
        if (riskVal === 4) return 'red';
        if (riskVal === 3) return 'yellow';
        if (riskVal === 2) return 'blue';
        if (riskVal === 1) return 'green';
        return 'gray';
      })();
      const riskText = (() => {
        if (riskVal === 1) return '> 1000m';
        if (riskVal === 2) return '600 - 1000m';
        if (riskVal === 3) return '350 - 600m';
        if (riskVal === 4) return '< 350m';
        return 'N/A';
      })();
      content = (
        <div style={{ fontFamily: 'Arial, sans-serif', padding: '5px', minWidth: '180px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <svg xmlns="http://www.w3.org/2000/svg" height="20" viewBox="0 0 24 24" width="20" fill="#000000">
                <path d="M0 0h24v24H0V0z" fill="none" />
                <path d="M21 16v-2l-8-5V4a1 1 0 0 0-2 0v5l-8 5v2l8-2.5V21l-2 1v1h6v-1l-2-1v-7.5z" />
              </svg>
              <span style={{ fontSize: '18px', fontWeight: 'bold' }}>{airport}</span>
            </div>
            <div style={{ width: '14px', height: '14px', borderRadius: '50%', backgroundColor: circleColor, border: '1px solid black' }}></div>
          </div>
          <div style={{ fontSize: '14px', marginBottom: '4px', color: '#333' }}>Visibility: <strong>{riskText}</strong></div>
          <div style={{ fontSize: '13px', color: '#007bff', cursor: 'pointer', fontWeight: '500' }}>Click for 48h forecast</div>
        </div>
      );
    }
    else if (activeLayer.startsWith('CNN')) {
      // Support both vector tiles (GRAY_INDEX) and GeoJSON (CONVECTIVE_MATRIX_RISK)
      const riskVal = props.GRAY_INDEX !== undefined ? props.GRAY_INDEX : props.CONVECTIVE_MATRIX_RISK;
      content = ConvectionTooltip({ riskLevel: riskVal, coordinates: formatCoordinates({ lat, lng }) });
    }
    else if (activeLayer.startsWith('ICE')) {
      content = IceTooltip({ riskLevel: props.ICE_MATRIX_RISK, coordinates: formatCoordinates({ lat, lng }) });
    }
    else if (activeLayer.startsWith('TURBULENCE')) {
      content = TurbulenceTooltip({ riskLevel: props.TURBULENCE_MATRIX_RISK, coordinates: formatCoordinates({ lat, lng }) });
    }
    else if (activeLayer.startsWith('DUST')) {
      content = DustTooltip({ riskLevel: props.DUST_MATRIX_RISK, coordinates: formatCoordinates({ lat, lng }) });
    }

    if (!content) return null;
    return <Popup longitude={lng} latitude={lat} closeButton={false} closeOnClick={false} anchor="top" maxWidth="300px" offset={10}>{content}</Popup>;
  };

  const renderClickPopup = () => {
    if (!clickedInfo || !clickedInfo.feature) return null;
    const { lng, lat } = clickedInfo.lngLat;
    const props = clickedInfo.feature.properties;

    if (activeLayer.startsWith('VISIBILITY') && props.AIRPORT) {
      return (
        <Popup longitude={lng} latitude={lat} anchor="bottom" onClose={() => setClickedInfo(null)} closeOnClick={false} maxWidth="500px">
          <div style={{ width: '450px', height: '300px', padding: '10px' }}><VisibilityGraph airport={props.AIRPORT} /></div>
        </Popup>
      );
    }

    // CNN live PMTiles Click popup
    if (isCnnLivePmtiles) {
      const riskVal = props.GRAY_INDEX !== undefined ? props.GRAY_INDEX : props.CONVECTIVE_MATRIX_RISK;
      const grayIndex = parseFloat(riskVal);
      const value = isNaN(grayIndex) ? 'N/A' : grayIndex.toFixed(2);
      const coords = formatCoordinates({ lat, lng });

      return (
        <Popup longitude={lng} latitude={lat} anchor="bottom" onClose={() => setClickedInfo(null)} closeOnClick={false} maxWidth={isCompact ? '280px' : '400px'}>
          <div style={{
            fontFamily: "'Inter', sans-serif",
            padding: isCompact ? '10px' : '14px',
            minWidth: isCompact ? '160px' : '220px',
            borderRadius: '16px',
            fontSize: isCompact ? '12px' : '14px',
            backgroundColor: 'rgba(255, 255, 255, 0.82)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            color: '#1a1a1a',
            border: '1px solid rgba(255, 255, 255, 0.6)',
            boxShadow: '0 12px 48px rgba(0,0,0,0.12)'
          }}>
            <div style={{
              backgroundColor: 'rgba(0, 0, 0, 0.06)',
              color: '#444',
              padding: '6px',
              fontWeight: '800',
              borderRadius: '10px',
              textAlign: 'center',
              marginBottom: isCompact ? '10px' : '14px',
              textTransform: 'uppercase',
              letterSpacing: '0.14em',
              fontSize: '11px'
            }}>
              Convection Risk
            </div>
            <div style={{ textAlign: 'center', marginBottom: isCompact ? '10px' : '14px' }}>
              <div style={{ fontSize: '11px', color: 'rgba(0,0,0,0.5)', marginBottom: '6px' }}>Risk Index</div>
              <span style={{ fontSize: isCompact ? '24px' : '32px', fontWeight: '900', color: '#1a1a1a', letterSpacing: '-0.03em' }}>{value}</span>
            </div>
            <table style={{ width: '100%', color: '#3152c2', fontSize: '10px', borderCollapse: 'collapse', fontWeight: '600' }}>
              <tbody>
                <tr><td style={{ padding: '2px 6px', opacity: 0.6 }}>LAT</td><td style={{ padding: '2px 6px', textAlign: 'right', fontWeight: '900' }}>{coords.lat}</td></tr>
                <tr><td style={{ padding: '2px 6px', opacity: 0.6 }}>LON</td><td style={{ padding: '2px 6px', textAlign: 'right', fontWeight: '900' }}>{coords.lng}</td></tr>
              </tbody>
            </table>
          </div>
        </Popup>
      );
    }

    return null;
  };

  const isMenuOpen = Boolean(anchorEl);
  const isIconOnly = windowWidth <= 1100;
  const isCompact = useMediaQuery('(max-width:600px)');
  const showText = !isIconOnly && !isCompact;
  const fontSizeInterior = windowWidth <= 500 ? "0.75rem" : "0.85rem";

  return (
    <>


      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Box className='layers-selector'>
          <Button
            onClick={handleClick}
            className={`layers-button ${isIconOnly ? 'icon-only' : ''}`}
            variant="contained"
            startIcon={<LayersIcon />}
            endIcon={showText ? (isMenuOpen ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />) : null}
            sx={{
              textTransform: 'none',
              fontWeight: 'bold',
              borderRadius: 3.5,
              backgroundColor: 'rgba(35, 155, 105, 0.8)',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              color: '#fff',
              minWidth: { xs: 44, md: 140 },
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                backgroundColor: 'rgba(35, 155, 105, 0.95)',
                transform: 'translateY(-2px)',
                boxShadow: '0 12px 40px rgba(35, 155, 105, 0.4)',
                borderColor: 'rgba(255, 255, 255, 0.4)'
              }
            }}
          >
            {showText && (buttonLabels[activeLayer] || 'Select Layer')}
          </Button>
          <Menu 
            id="layers-menu" 
            anchorEl={anchorEl} 
            open={Boolean(anchorEl)} 
            onClose={handleClose} 
            keepMounted 
            MenuListProps={{ dense: true }} 
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            PaperProps={{ 
              sx: { 
                backgroundColor: 'rgba(0,0,0,0.75)', 
                color: '#fff', 
                borderRadius: '12px', 
                backdropFilter: 'blur(2px)', 
                transform: 'translateY(-25px) !important',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.72)' 
              } 
            }}
          >
            {[
              'CNN:EU_025_live_demo', 'CNN:EU_01_live_demo', 'CNN:euro_1k_00625_15m',
              'VISIBILITY:visibility_live_demo', 'NOWCASTING:nowcastnet_live_demo',
              'ICE:ICE_025_live_demo', 'TURBULENCE:TURBULENCE_0125_live_demo', 'DUST:DUST_025_live_demo'
            ].map((layerName) => (
              <MenuItem key={layerName} onClick={() => handleMenuItemClick(layerName)} selected={layerName === activeLayer} sx={{ gap: '8px', '&.Mui-selected': { backgroundColor: 'rgba(255,255,255,0.12)' } }}>
                <ListItemIcon sx={{ minWidth: 32 }}><Radio checked={layerName === activeLayer} sx={{ color: '#bbb', '&.Mui-checked': { color: '#4caf50' } }} /></ListItemIcon>
                <Typography variant="body1" sx={{ color: '#fff', fontSize: fontSizeInterior, fontWeight: layerName === activeLayer ? 700 : 500 }}>{buttonLabels[layerName] || layerName}</Typography>
              </MenuItem>
            ))}
          </Menu>
        </Box>
        <Box
          sx={{
            position: 'absolute',
            bottom: 55,
            left: 5,
            display: 'flex',
            flexDirection: 'column-reverse',
            gap: 2,
            alignItems: 'center',
            zIndex: 10000,
          }}
        >
          
          {(isCnnLayer || hasHeatmap) && (
            <Badge
              badgeContent={
                <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.55rem' }}>
                  HEATMAPS
                </Typography>
              }
              color="error"
              overlap="circular"
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              sx={{
                '& .MuiBadge-badge': {
                  padding: '0 4px',
                  height: '16px',
                  minWidth: '30px',
                  top: '0px',
                  right: '-7px'
                },
              }}
            >
              <IconButton
                onClick={() => setShowMosaic(!showMosaic)}
                sx={{
                  bgcolor: showMosaic ? '#38a477' : '#ffffff',
                  color: showMosaic ? '#ffffff' : '#38a477',
                  '&:hover': { bgcolor: showMosaic ? '#38a477' : '#f5f5f5' },
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
                  width: { xs: 38, md: 42 },
                  height: { xs: 38, md: 42 },
                  border: '1px solid rgba(0, 0, 0, 0.05)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  backdropFilter: 'blur(16px)',
                  pointerEvents: 'auto',
                  marginBottom: '18px',
                  marginLeft: '7.5px',
                  borderRadius: 10
                }}
                title="Toggle Heatmap Layer"
              >
                <LayersIcon sx={{ fontSize: { xs: 18, md: 22 } }} />
              </IconButton>
            </Badge>
          )}

          {/* FIRs/UIRs: se ven en todas las capas. Sin HEATMAPS/POLYGONS ocupa su hueco (mismo marginBottom) */}
          <Badge
            badgeContent={
              <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.55rem' }}>
                SECTORS
              </Typography>
            }
            color="error"
            overlap="circular"
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            sx={{
              '& .MuiBadge-badge': {
                padding: '0 4px',
                height: '16px',
                minWidth: '30px',
                top: '0px',
                right: '-7px'
              },
            }}
          >
            <IconButton
              onClick={() => setShowFirs(!showFirs)}
              aria-pressed={showFirs}
              sx={{
                bgcolor: showFirs ? '#38a477' : '#ffffff',
                color: showFirs ? '#ffffff' : '#38a477',
                '&:hover': { bgcolor: showFirs ? '#38a477' : '#f5f5f5' },
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
                width: { xs: 38, md: 42 },
                height: { xs: 38, md: 42 },
                border: '1px solid rgba(0, 0, 0, 0.05)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                backdropFilter: 'blur(16px)',
                pointerEvents: 'auto',
                marginBottom: (isCnnLayer || hasHeatmap) ? '2.5px' : '18px',
                marginLeft: '7.5px',
                borderRadius: 10
              }}
              title="Toggle FIR/UIR sectors"
            >
              <Grid4x4Icon sx={{ fontSize: { xs: 18, md: 22 } }} />
            </IconButton>
          </Badge>

          {(isCnnLayer || hasHeatmap) && (
            <Badge
              badgeContent={
                <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.55rem' }}>
                  POLYGONS
                </Typography>
              }
              color="error"
              overlap="circular"
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              sx={{
                '& .MuiBadge-badge': {
                  padding: '0 4px',
                  height: '16px',
                  minWidth: '30px',
                  top: '0px',
                  right: '-7px'
                },
              }}
            >
              <IconButton
                onClick={() => setShowGeoJson(!showGeoJson)}
                disabled={polygonsComingSoon}
                sx={{
                  bgcolor: showGeoJson ? '#38a477' : '#ffffff',
                  color: showGeoJson ? '#ffffff' : '#38a477',
                  '&:hover': { bgcolor: showGeoJson ? '#38a477' : '#f5f5f5' },
                  '&.Mui-disabled': { bgcolor: '#ffffff', color: '#38a477', opacity: 0.4 },
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
                  width: { xs: 38, md: 42 },
                  height: { xs: 38, md: 42 },
                  border: '1px solid rgba(0, 0, 0, 0.05)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  backdropFilter: 'blur(16px)',
                  pointerEvents: 'auto',
                  marginBottom: '2.5px',
                  marginLeft: '7.5px',
                  borderRadius: 10
                }}
                title={polygonsComingSoon ? 'Polygons (coming soon)' : 'Toggle Polygons Layer'}
              >
                <MapIcon sx={{ fontSize: { xs: 18, md: 22 } }} />
              </IconButton>
            </Badge>
          )}
        </Box>
      </Box>

      {/* PMTILES LAYER for CNN 025 & 01 */}
      {hasHeatmap && showMosaic && currentPmtileFile && pmtilesConfig && (
        <Source
          id="pmtiles-cnn"
          type="raster"
          url={`pmtiles://${pmtilesConfig.baseUrl}${currentPmtileFile}`}
          tileSize={256}
        >
          <Layer
            id="pmtiles-cnn-raster"
            type="raster"
            paint={{
              'raster-opacity': opacity,
              'raster-fade-duration': 0,
              // ICE son 3 clases (3 colores exactos, teselas solo hasta z2): 'linear' las emborrona e inventa tonos
              // intermedios, igual que en dust. CNN y turbulence son heatmaps continuos: linear
              'raster-resampling': activeLayer.startsWith('ICE') ? 'nearest' : 'linear'
            }}
          />
        </Source>
      )}

      {/* RASTER LAYERS - Gestionadas imperativamente por useBufferedWmsLayers */}

      {/* GEOJSON LAYERS */}
      {showPolygons && vectorSourceData && (
        <Source type="geojson" data={vectorSourceData}>
          {activeLayer.startsWith('VISIBILITY') && (
            <Layer id="visibility-circle" type="circle" paint={{
              'circle-radius': 4.35,
              'circle-stroke-color': '#000000',
              'circle-stroke-width': 1.25,
              'circle-color': ['match', ['to-number', ['get', 'VISIBILITY_MATRIX_RISK']], 4, 'red', 3, 'yellow', 2, 'blue', 1, 'green', 'gray'],
              'circle-opacity': 1
            }} />
          )}
          {activeLayer.startsWith('CNN') && (
            <Layer id="cnn-fill" type="fill" paint={{
              'fill-color': [
                'match',
                ['get', 'CONVECTIVE_MATRIX_RISK'],
                1, '#ffff00', // 1: Low
                2, '#FFA500', // 2: Medium
                3, '#DC143C', // 3: High
                'transparent' // Fallback para cualquier otro valor
              ],
              'fill-opacity': opacity,
              'fill-outline-color': '#a3a1a1c0'
              // 'fill-outline-color': 'transparent'
            }} />
          )}
          {activeLayer.startsWith('ICE') && (
            <Layer id="ice-fill" type="fill" paint={{
              'fill-color': ['match', ['get', 'ICE_MATRIX_RISK'], 1, '#7fd4f9', 2, '#1664c0', 3, '#311c93', 'transparent'],
              'fill-opacity': opacity
            }} />
          )}
          {activeLayer.startsWith('TURBULENCE') && (
            <Layer id="turbulence-fill" type="fill" paint={{
              'fill-color': ['match', ['get', 'TURBULENCE_MATRIX_RISK'], 1, '#ffd700', 2, '#ff0000', 3, '#ff0000', 'transparent'],
              'fill-opacity': opacity
            }} />
          )}
          {activeLayer.startsWith('DUST') && (
            <Layer id="dust-fill" type="fill" paint={{
              'fill-color': ['match', ['get', 'DUST_MATRIX_RISK'], 1, '#d2bfa8', 2, '#967157', 3, '#3f2b21', 'transparent'],
              'fill-opacity': opacity
            }} />
          )}
        </Source>
      )}

      {renderPopup()}
      {renderClickPopup()}

      {/* PRELOADING - Gestionado por la caché de 6 capas del hook imperativo */}
    </>
  );
};

export default MapLayers;
